/**
 * Design tokens for the app shell, ported from the "App Shell Redesign" mock.
 *
 * DefaultLayout writes these onto the shell root as CSS variables (--surface,
 * --border, …) so inline styles and <style> rules can read them via var(--x).
 * They live in their own module because screens that wrap part of themselves in
 * a nested antd ConfigProvider need the same values as plain hex: dropdown
 * panels, date pickers and modals portal to <body>, outside the element the CSS
 * variables are defined on, so var(--x) cannot reach them.
 *
 * KEEP IN STEP WITH styles/tokens.css, which republishes this palette for the
 * stylesheets that cannot import from JS (the screen CSS files alias it rather
 * than transcribing hexes). The two cannot be collapsed: antd needs plain hex,
 * CSS needs var(). Change a hue here and change it there.
 */

const readAccent = () => {
  try { return localStorage.getItem("ACCENT_COLOR") || "#d97706"; }
  catch { return "#d97706"; }
};
export const PRIMARY = readAccent();

export const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1,3), 16);
  const g = parseInt(hex.slice(3,5), 16);
  const b = parseInt(hex.slice(5,7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

const darkenHex = (hex, amount) => {
  const r = Math.max(0, parseInt(hex.slice(1,3), 16) - amount);
  const g = Math.max(0, parseInt(hex.slice(3,5), 16) - amount);
  const b = Math.max(0, parseInt(hex.slice(5,7), 16) - amount);
  return "#" + [r,g,b].map(c => c.toString(16).padStart(2,"0")).join("");
};

/**
 * The focus halo for an accent. Falls back to the default accent when the
 * stored one is not a 6-digit hex — an unparseable colour would yield
 * `rgba(NaN,…)` and drop the ring entirely rather than fail loudly.
 */
export const ringOf = (hex, alpha = 0.12) =>
  hexToRgba(/^#[0-9a-fA-F]{6}$/.test(hex || "") ? hex : PRIMARY, alpha);

export const SHELL_THEME = {
  dark: {
    page: "#06080c", bg: "#0a0d13", sidebar: "#0c1017", header: "#0c1017",
    surface: "#11151c", surface2: "#1a1f29", border: "#232935", borderStrong: "#2f3746",
    hover: "#161c26", text: "#e8ebf1", muted: "#8a93a3", dim: "#616b7c",
    crumb: "#a8b2c2", sep: "#3d4655", activeBg: hexToRgba(PRIMARY, .16), activeText: darkenHex(PRIMARY, 20),
  },
  light: {
    page: "#e7ebf1", bg: "#f5f7fa", sidebar: "#ffffff", header: "#ffffff",
    surface: "#ffffff", surface2: "#f1f4f8", border: "#e2e7ef", borderStrong: "#cbd3e0",
    hover: "#eef2f7", text: "#0f1620", muted: "#5b6472", dim: "#8b95a5",
    crumb: "#4f5866", sep: "#c3cbd8", activeBg: hexToRgba(PRIMARY, .12), activeText: darkenHex(PRIMARY, 40),
  },
};

/**
 * The palette as inline-style CSS variables, for a screen root.
 *
 * Every redesigned screen re-declares the shell's variables on its own root so
 * that a Drawer or Popover portalled to <body> can be handed the same names.
 * One helper rather than the same Object.fromEntries in each screen.
 */
export const shellVars = (darkMode) => ({
  ...Object.fromEntries(
    Object.entries(SHELL_THEME[darkMode ? "dark" : "light"]).map(([k, v]) => [`--${k}`, v]),
  ),
  "--primary": PRIMARY,
  // The focus halo, derived from the accent rather than hardcoded — the accent
  // is configurable, and an amber ring around a green-bordered input was the
  // giveaway that this had been written as a literal.
  "--primaryRing": ringOf(PRIMARY),
});

/** Status hues shared by the order form's readiness rail and product rows. */
export const STATUS = {
  ok: "#34d399",
  warn: "#fbbf24",
  error: "#f87171",
  errorSoft: "#fca5a5",
};

export const MONO = "'JetBrains Mono',ui-monospace,SFMono-Regular,Menlo,monospace";

/**
 * Nested antd theme for screens that adopt the shell's palette.
 *
 * The shell publishes these as CSS variables, but dropdown panels, date-picker
 * popups and modals portal to <body> where var(--x) does not resolve — so antd
 * gets the same tokens as hex. One mapping, shared by every redesigned screen.
 */
export const shellAntdTheme = (darkMode) => {
  const t = SHELL_THEME[darkMode ? "dark" : "light"];
  const accent = localStorage.getItem("ACCENT_COLOR") || PRIMARY;
  return {
    token: {
      colorPrimary: accent,
      colorBgContainer: t.surface2,
      colorBgElevated: darkMode ? t.surface2 : t.surface,
      colorBorder: t.border,
      colorBorderSecondary: t.border,
      colorSplit: t.border,
      // The shell has its own status hues; without these antd falls back to its
      // stock red/amber/green, which is what made a `status="warning"` input's
      // border read as foreign against these surfaces. antd derives the rest of
      // each palette (hover, active, background) from the base colour.
      colorSuccess: STATUS.ok,
      colorWarning: STATUS.warn,
      colorError: STATUS.error,
      colorText: t.text,
      colorTextHeading: t.text,
      colorTextSecondary: t.muted,
      colorTextDescription: t.muted,
      colorTextPlaceholder: t.dim,
      colorIcon: t.dim,
      colorIconHover: t.text,
      controlHeight: 38,
      controlHeightSM: 30,
      controlHeightLG: 44,
      controlItemBgHover: t.hover,
      controlItemBgActive: t.activeBg,
      borderRadius: 8,
      borderRadiusLG: 12,
      fontFamily: "Outfit, system-ui, sans-serif",
      fontSize: 13.5,
    },
  };
};
